import { Input, Spin, notification } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocation, useNavigate } from "react-router-dom";
import AppRoutes from "../../common/routes";
import {
  _verify_user_email_signup,
  _resend_verification_code,
} from "../../common/axios_services";
import { ReactComponent as ArrowLeftIcon } from "../../assets/images/icons/arrow-left-circle.svg";

const VerificationPage = () => {
  const Navigate = useNavigate();
  const antIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#fff" }} spin />
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [fetchingData, setFetchingData] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const location = useLocation();
  const emailRef = useRef(null);
  const passcodeRef = useRef(null);

  const signInCheck = yup.object().shape({
    email: yup
      .string()
      .email("Email address is not valid")
      .required("Email address field is required"),
    verificationCode: yup
      .string()
      .required("Verification code is required")
      .length(6, "Verification code must be 6 digits"),
  });

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValue: {
      email: "",
      password: "",
    },
    resolver: yupResolver(signInCheck),
  });

  const openNotificationWithIcon = (type, message) => {
    notification[type]({
      message: "",
      description: message,
    });
  };

  const verifyUser = async (e) => {
    setErrorMessage("");
    setFetchingData(true);
    let { email, verificationCode } = e;
    try {
      const response = await _verify_user_email_signup({
        email,
        verificationCode,
      });
      openNotificationWithIcon(
        "success",
        "Email address verified successfully"
      );
      Navigate(AppRoutes.signin);
    } catch (err) {
      setFetchingData(false);
      if (err.response) {
        setErrorMessage(err.response.data.message);
      } else {
        setErrorMessage(err.message);
      }
    }
  };

  const handleResendCode = async () => {
    const email = getValues("email");
    if (!email) {
      openNotificationWithIcon(
        "error",
        "Please enter your email address first."
      );
      return;
    }
    setIsResending(true);
    try {
      await _resend_verification_code({ email });
      openNotificationWithIcon(
        "success",
        "A new verification code has been sent."
      );
      setResendCooldown(60);
    } catch (err) {
      openNotificationWithIcon(
        "error",
        err.response?.data?.message ||
          "Failed to resend code. Please try again."
      );
    }
    setIsResending(false);
  };

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(
        () => setResendCooldown(resendCooldown - 1),
        1000
      );
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    if (location.pathname === "/verify-email") {
      const savedEmail = localStorage.getItem("pendingVerificationEmail");
      if (savedEmail) {
        setValue("email", savedEmail);
      }
    }
  }, [location.pathname, setValue]);

  useEffect(() => {
    // Auto-focus logic
    if (
      location.pathname === "/verify-email" ||
      location.pathname === "/verify-passcode"
    ) {
      if (passcodeRef.current) {
        passcodeRef.current.focus();
      }
    } else {
      if (emailRef.current) {
        emailRef.current.focus();
      }
    }
  }, [location.pathname]);

  return (
    <div className="signin-page verification_page">
      <div>
        <div className="small-page-title grid-flex">
          <div onClick={() => Navigate(-1)}>
            <ArrowLeftIcon />
          </div>
          <div></div>
        </div>
      </div>
      <div className="contain auth-content">
        <div className="mt_3">
          <div>
            <h3 className="auth-sect-title">Verify email by passcode</h3>
            <p>
              We have sent a verification code to your email address. Please
              enter the verification code to complete your signup process.
            </p>
          </div>
          <div className="error-block">
            {errorMessage.length ? (
              <p className="form-error">{errorMessage}</p>
            ) : (
              ""
            )}
          </div>
          <form onSubmit={handleSubmit(verifyUser)}>
            <div className="form_group">
              <label htmlFor="email">Email address</label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    name="email"
                    {...field}
                    id="email"
                    type="email"
                    ref={emailRef}
                    style={{ height: "5rem" }}
                  />
                )}
              />
              {errors.email && (
                <p className="form-error">{errors.email.message}</p>
              )}
            </div>
            <div className="form_group">
              <label htmlFor="verificationCode">Verification Code</label>
              <Controller
                name="verificationCode"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    ref={passcodeRef}
                    autoComplete="off"
                    name="verificationCode"
                    id="verificationCode"
                    style={{ height: "5rem" }}
                  />
                )}
              />
              {errors.verificationCode && (
                <p className="form-error">{errors.verificationCode.message}</p>
              )}
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending || resendCooldown > 0}
                className="underline disabled:text-gray-400 disabled:no-underline"
              >
                {isResending
                  ? "Sending..."
                  : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : "Resend Code"}
              </button>
            </div>

            <div className="welcome-buttons mt_20">
              <div className="welcome-buttons-content">
                <div>
                  {!fetchingData ? (
                    <button className="btn-primary">Verify Email</button>
                  ) : (
                    <button disabled className="btn-primary">
                      <Spin indicator={antIcon} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VerificationPage;
